import React, { useEffect, useMemo, useState } from 'react'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import {
  BASIC_DROPDOWN_ALIGN,
  BASIC_DROPDOWN_DROP,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.constants'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useBasicDropdown } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import isFunction from 'lodash/isFunction'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import palette from 'src/designSystem/variables/palette'
import {
  ControlStatusDropdownOptions,
  ControlStatusOptions,
  findCustomControlStatus,
} from './Dropdowns.constants'

export const ControlStatusDropdown = ({
  control,
  initialStatus,
  onSelect,
  isDisabled,
}: {
  control: Control.AsObject
  initialStatus: CONTROL_STATUS
  onSelect?: (
    status: CONTROL_STATUS,
    customStatusId?: string,
    name?: string,
  ) => void
  isDisabled?: boolean
}): JSX.Element => {
  const { openDropdownId, setOpenDropdownId } = useBasicDropdown()
  const dropdownId = `control-status-${control.modelId}`
  const isOpen = openDropdownId === dropdownId
  const canUpdateStatus = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_UPDATE_CONTROL_STATUS,
    control,
  )
  const { data: customStatuses } = useControlStatuses()
  const isDropdownisabled = !canUpdateStatus || isDisabled

  // Shared with the index page search, so a row is found by the status it shows.
  const shownStatus = (): BasicDropdownItem => {
    const customStatus = findCustomControlStatus(
      control.customStatusId,
      customStatuses?.getItemsList(),
    )
    return customStatus
      ? { name: customStatus.getName(), value: customStatus.getId() }
      : {
          name: ControlStatusOptions[initialStatus as CONTROL_STATUS],
          value: initialStatus,
        }
  }

  const [selectedItem, setSelectedItem] =
    useState<BasicDropdownItem>(shownStatus)

  useEffect(() => {
    setSelectedItem(shownStatus())
    // shownStatus reads only the values listed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStatuses, control.customStatusId, initialStatus])

  const customOptions = useMemo(
    () =>
      customStatuses
        ?.getItemsList()
        .filter((status) => status.getName())
        .map(
          (status) =>
            ({
              name: status.getName(),
              value: status.getId(),
            } as BasicDropdownItem),
        ) || [],
    [customStatuses],
  )

  const items = customOptions

  const updateStatus = async (item: BasicDropdownItem) => {
    setSelectedItem(item)
    if (isFunction(onSelect)) {
      if (typeof item.value === 'string') {
        onSelect(control.status, item.value, item.name as string)
      } else {
        onSelect(item.value as CONTROL_STATUS, '', item.name as string)
      }
    }
  }

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setOpenDropdownId(dropdownId)
    } else {
      setOpenDropdownId(null)
    }
  }

  const selectedStatusColors = useMemo(() => {
    if (typeof selectedItem.value === 'string') {
      const customStatus = customStatuses
        ?.getItemsList()
        .find((s) => s.getId() === selectedItem.value)
      if (customStatus?.getColor()) {
        return {
          background: customStatus.getColor(),
          border: customStatus.getBorderColor() || customStatus.getColor(),
        }
      }
    }
    const option = ControlStatusDropdownOptions.find(
      (option) => option.key === selectedItem.value,
    )
    return (
      option?.colors || {
        background: palette.neutral.white,
        border: palette.neutral['300'],
      }
    )
  }, [selectedItem.value, customStatuses])

  return (
    <BasicDropdown
      selectedItem={selectedItem}
      items={items}
      onSelect={updateStatus}
      drop={BASIC_DROPDOWN_DROP.DOWN}
      align={BASIC_DROPDOWN_ALIGN.END}
      backgroundColor={selectedStatusColors.background}
      borderColor={selectedStatusColors.border}
      onToggle={handleToggle}
      show={isOpen}
      offset={4}
      disabled={
        isDropdownisabled || selectedItem.value === CONTROL_STATUS.NOTAPPLICABLE
      }
    />
  )
}
